import Vue from 'vue'
import { getVariant } from './feature-toggles.module'

const isHttpUrl = (value) => {
  try {
    const { protocol } = new URL(value)
    return protocol === 'https:' || protocol === 'http:'
  } catch (error) {
    return false
  }
}

const RETAKE_PROGRESS_NUMERATOR = 4
const RETAKE_PROGRESS_DENOMINATOR = 5

const initialState = () => ({
  sessionEnded: null,
  retakeLoginTouchpoint: null,
  isRetakeUnlocked: false,
})

const mutations = {
  openRetakeLogin(sessionState, touchpoint) {
    sessionState.retakeLoginTouchpoint = touchpoint
  },
  closeRetakeLogin(sessionState) {
    sessionState.retakeLoginTouchpoint = null
  },
  unlockRetake(sessionState) {
    sessionState.isRetakeUnlocked = true
  },
  resetSessionEnded(sessionState) {
    sessionState.sessionEnded = null
    sessionState.isRetakeUnlocked = false
  },
  setSessionEnded(sessionState, payload) {
    if (!payload || !isHttpUrl(payload.replayUrl)) return
    if (payload.source === 'session_ended') sessionState.isRetakeUnlocked = true
    if (sessionState.sessionEnded) return
    Vue.set(sessionState, 'sessionEnded', {
      sessionId: payload.sessionId,
      replayUrl: payload.replayUrl,
      source: payload.source,
    })
  },
}

const replayFromPresentation = (presentation) => {
  const replay = presentation && presentation.replayLink
  if (!replay || !isHttpUrl(replay.replayUrl)) return null
  return { sessionId: replay.sessionId, replayUrl: replay.replayUrl, source: 'presentation_load' }
}

const getters = {
  sessionEnded: (sessionState) => sessionState.sessionEnded,
  hasPassedRetakePoint: (_, __, ___, rootGetters) => {
    const slides = rootGetters['slideProgress/visibleSlides'] || []
    const activeSlideId = rootGetters.activeActivity?.id
    const position = slides.findIndex((slide) => slide.id === activeSlideId) + 1
    return position > 0 && position * RETAKE_PROGRESS_DENOMINATOR >= slides.length * RETAKE_PROGRESS_NUMERATOR
  },
  // An explicit presenter choice wins; a deck that never set it follows its Review setting.
  isRetakeOn: (_, __, rootState) => {
    const value = rootState.presentationAttributeModule?.presentationAttribute?.replayEnabled
    if (typeof value === 'boolean') return value
    return !!rootState.presentation?.isEnableAudienceReviewSlides
  },
  // The stored link survives from an earlier session, so it only counts once this session passes the retake point or ends.
  availableReplay: (sessionState, moduleGetters, rootState) => {
    if (!sessionState.isRetakeUnlocked && !moduleGetters.hasPassedRetakePoint) return null
    return sessionState.sessionEnded || replayFromPresentation(rootState.presentation)
  },
  retake: (_, moduleGetters, __, rootGetters) => {
    if (!moduleGetters.isEnabled || moduleGetters.isReplayCopy || !moduleGetters.isRetakeOn) return null
    if (rootGetters['participantEmbed/isParticipantEmbed']) return null
    return moduleGetters.availableReplay
  },
  isEnabled: (_, __, ___, rootGetters) => getVariant(rootGetters, 'AHA-49118-replay')?.name === 'treatment',
  retakeSessionId: (sessionState, _, rootState) => (
    sessionState.sessionEnded?.sessionId || rootState.presentation?.replayLink?.sessionId || null
  ),
  isRetakePlayer: (_, moduleGetters) => !!moduleGetters.isEnabled && !!moduleGetters.isReplayCopy,
  isRetakeSessionOver: (_, moduleGetters, rootState) => {
    if (!moduleGetters.retake) return false
    return rootState.presentation?.presenting === false && !rootState.presentation?.audiencePacing
  },
  isReplayCopy: (_, __, rootState) => {
    const value = rootState.presentationAttributeModule?.presentationAttribute?.isReplayCopy
    return value === true || value?.enabled === true
  },
}

export default {
  namespaced: true,
  state: initialState,
  mutations,
  getters,
}
